export enum ChatSender {
  CUSTOMER = 'customer',
  ADMIN = 'admin',
  SYSTEM = 'system'
}

export enum ChatMessageStatus {
  SENT = 'sent',
  DELIVERED = 'delivered',
  READ = 'read',
  FAILED = 'failed',
  SENDING = 'sending'
}

export enum AttachmentKind {
  IMAGE = 'image',
  DOCUMENT = 'document',
  AUDIO = 'audio'
}

export interface ChatAttachment {
  kind: AttachmentKind;
  minioObjectKey: string;
  mimeType: string;
  sizeBytes: number;
  originalName?: string;
  telegramFileId?: string;
  // Duration in seconds, only meaningful for AUDIO attachments.
  durationSec?: number;
  // local-only: object URL used to render an instant preview for optimistic
  // image / audio messages while the upload is in flight.
  localPreviewUrl?: string;
}

export interface ReplyToPreview {
  _id: string;
  sender: ChatSender;
  text?: string;
  hasAttachments?: boolean;
}

export interface ChatTicket {
  status: 'pending' | 'paid' | 'confirmed' | 'rejected' | 'expired';
  amount: number;
  currency: string;
  network?: string;
  walletAddress?: string;
  targetAccount?: string;
  fiatEquivalent?: { amount: number; currency: string };
  reference?: string;
  expiresAt?: string;
  issuedAt?: string;
  note?: string;
  customerName?: string;
  customerEmail?: string;
  baseAmount?: number;
  baseCurrency?: string;
  feePercent?: number;
  feeAmount?: number;
  createdByTelegramUserId?: number;
  createdByTelegramUsername?: string;
}

export interface ChatMessage {
  _id?: string;
  id?: string;
  conversationId?: string;
  sender: ChatSender;
  text?: string;
  attachments?: ChatAttachment[];
  attachmentsPurged?: boolean;
  editedAt?: string;
  ticket?: ChatTicket;
  telegramMessageId?: number;
  replyToMessageId?: string;
  replyTo?: ReplyToPreview;
  status: ChatMessageStatus;
  createdAt: string;
  updatedAt?: string;
  // local-only fields for optimistic insert
  localId?: string;
  // Retained on the optimistic message so a failed send can be retried
  // without forcing the user to re-pick / re-record. Cleared as soon as the
  // message transitions to SENT / DELIVERED so the File objects can be
  // garbage-collected.
  pendingFiles?: File[];
  pendingAudioDurationSec?: number;
}

export enum ConversationStatus {
  OPEN = 'open',
  CLOSED = 'closed'
}

export interface Conversation {
  _id?: string;
  id?: string;
  customerId: string;
  customerEmail: string;
  customerDisplayName?: string;
  threadId?: number;
  status?: ConversationStatus;
  closedAt?: string;
  lastMessageAt?: string;
  unreadByCustomer: number;
  unreadByAdmin: number;
  lastAdminActivityAt?: string;
}

export interface ListMessagesResponse {
  conversation: Conversation;
  messages: ChatMessage[];
  hasMore: boolean;
}

export type AgentStatus = 'active' | 'inactive';

export type SupportChatStreamEvent =
  | { type: 'message'; message: ChatMessage }
  | { type: 'typing'; from: 'admin'; ttlMs?: number }
  | { type: 'read'; untilMessageId?: string }
  | { type: 'presence'; online: boolean; since?: string; status?: AgentStatus }
  | { type: 'conversation'; conversation: Conversation }
  | { type: 'delete'; messageId: string }
  | { type: 'heartbeat'; timestamp: number };
